import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Twitter, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useBranding } from "@/contexts/BrandingContext";
import { useTenant } from "@/contexts/TenantContext";
import { useSitePages } from "@/hooks/useSitePages";
import { marketplaceSupabase } from "@/integrations/supabase/marketplaceClient";
import logoImage from "@/assets/logo.png";

export function MarketplaceFooter() {
  const { branding } = useBranding();
  const { tenantId, tenant } = useTenant();
  const { data: sitePages = [] } = useSitePages(tenantId);

  const [contactData, setContactData] = useState<{
    phone?: string;
    whatsapp?: string;
    email?: string;
    address?: string;
    creci?: string;
    instagramUrl?: string;
    facebookUrl?: string;
    linkedinUrl?: string;
    youtubeUrl?: string;
  }>({});

  useEffect(() => {
    const loadContactData = async () => {
      if (!tenantId) return;

      try {
        // Fetch realtor data for social links and primary contact
        const { data: realtorData } = await marketplaceSupabase
          .from('realtors')
          .select('*')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .order('is_primary', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Fetch business profile for address
        const { data: businessData } = await marketplaceSupabase
          .from('business_profile')
          .select('address')
          .eq('tenant_id', tenantId)
          .maybeSingle();

        setContactData({
          phone: realtorData?.phone || undefined,
          whatsapp: realtorData?.whatsapp || undefined,
          email: realtorData?.email || undefined,
          address: businessData?.address || undefined,
          creci: realtorData?.creci || undefined,
          instagramUrl: realtorData?.instagram_url || undefined,
          facebookUrl: realtorData?.facebook_url || undefined,
          linkedinUrl: realtorData?.linkedin_url || undefined,
          youtubeUrl: realtorData?.youtube_url || undefined,
        });

      } catch (err) {
        console.error('[MarketplaceFooter] Error loading contact data:', err);
      }
    };

    loadContactData();
  }, [tenantId]);

  // Gera URLs baseadas no tenant slug ou subdomínio
  const getUrl = (path: string) => {
    const hostname = window.location.hostname;
    const tenantDomain = 'imobpanda.site';
    const cleanHost = hostname.replace(/^www\./, '');

    if (cleanHost !== tenantDomain && cleanHost.endsWith(tenantDomain)) {
      return path === '' ? '/' : path;
    }

    if (tenant?.subdomain) {
      return `/${tenant.subdomain}${path === '' ? '' : path}`;
    }

    return path === '' ? '/marketplace' : path;
  };

  const ensureUrlScheme = (url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  if (!branding) {
    return null;
  }

  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full relative overflow-hidden bg-background border-t border-border/50 text-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          {/* Coluna 1: Logo, Descrição e Redes Sociais */}
          <div className="lg:col-span-1 flex flex-col items-start">
            <Link to={getUrl('')} className="flex items-center gap-3 mb-6 group inline-block">
              {(branding.header_style === 'logo' || branding.header_style === 'logo_only' || !branding.header_style) && branding.header_logo_url && (
                <img
                  src={branding.header_logo_url}
                  alt={branding.header_text || branding.display_name || "Logo"}
                  width="140"
                  height="40"
                  className="h-10 w-auto object-contain rounded-lg group-hover:opacity-90 transition-opacity"
                />
              )}
              {(branding.header_style === 'logo' || branding.header_style === 'text_only' || (!branding.header_style && (!branding.header_logo_url || branding.header_text))) && (branding.header_text || branding.display_name) && (
                <span className="text-2xl font-bold tracking-tight text-foreground group-hover:text-[var(--brand-primary)] transition-colors">
                  {branding.header_text || branding.display_name}
                </span>
              )}
            </Link>

            <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-medium">
              {branding.hero_description || "Conectamos pessoas aos seus lares dos sonhos com tecnologia avançada e atendimento personalizado de excelência."}
            </p>

            {/* Social Links */}
            <div className="flex gap-2.5">
              {contactData.instagramUrl && (
                <a href={ensureUrlScheme(contactData.instagramUrl)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="h-10 w-10 rounded-full hover:bg-[var(--brand-primary)] hover:text-white hover:border-transparent transition-all shadow-sm">
                    <Instagram className="h-4 w-4" />
                  </Button>
                </a>
              )}
              {contactData.facebookUrl && (
                <a href={ensureUrlScheme(contactData.facebookUrl)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="h-10 w-10 rounded-full hover:bg-[var(--brand-primary)] hover:text-white hover:border-transparent transition-all shadow-sm">
                    <Facebook className="h-4 w-4" />
                  </Button>
                </a>
              )}
              {contactData.linkedinUrl && (
                <a href={ensureUrlScheme(contactData.linkedinUrl)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="h-10 w-10 rounded-full hover:bg-[var(--brand-primary)] hover:text-white hover:border-transparent transition-all shadow-sm">
                    <Linkedin className="h-4 w-4" />
                  </Button>
                </a>
              )}
              {contactData.youtubeUrl && (
                <a href={ensureUrlScheme(contactData.youtubeUrl)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="h-10 w-10 rounded-full hover:bg-[var(--brand-primary)] hover:text-white hover:border-transparent transition-all shadow-sm">
                    <Youtube className="h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>
          </div>

          {/* Coluna 2: Navegação */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-5 tracking-tight">Navegação</h3>
            <ul className="space-y-3">
              <li>
                <Link to={getUrl('')} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  Início
                </Link>
              </li>
              <li>
                <Link to={getUrl('/properties')} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  Imóveis
                </Link>
              </li>
              {sitePages.map(page => (
                <li key={page.id}>
                  <Link to={getUrl(`/#${page.slug}`)} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Coluna 3: Serviços */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-5 tracking-tight">Serviços</h3>
            <ul className="space-y-3">
              <li>
                <Link to={getUrl('/properties?finalidade=venda')} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  Comprar Imóvel
                </Link>
              </li>
              <li>
                <Link to={getUrl('/properties?finalidade=aluguel')} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  Alugar Imóvel
                </Link>
              </li>
              <li>
                <Link to={getUrl('/#contato')} className="text-sm font-medium text-muted-foreground hover:text-[var(--brand-primary)] transition-colors flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  Vender seu Imóvel
                </Link>
              </li>
            </ul>
          </div>

          {/* Coluna 4: Contato */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-5 tracking-tight">Contato</h3>
            <div className="space-y-4">
              {contactData.whatsapp && (
                <div className="flex items-start gap-3 group">
                  <div className="p-2 rounded-lg bg-muted group-hover:bg-[var(--brand-primary)]/10 transition-colors shrink-0">
                    <Phone className="h-4 w-4 text-foreground group-hover:text-[var(--brand-primary)] transition-colors" />
                  </div>
                  <div className="pt-1.5">
                    <a href={`https://wa.me/55${contactData.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors block">
                      {contactData.whatsapp}
                    </a>
                  </div>
                </div>
              )}

              {contactData.email && (
                <div className="flex items-start gap-3 group">
                  <div className="p-2 rounded-lg bg-muted group-hover:bg-[var(--brand-primary)]/10 transition-colors shrink-0">
                    <Mail className="h-4 w-4 text-foreground group-hover:text-[var(--brand-primary)] transition-colors" />
                  </div>
                  <div className="pt-1.5">
                    <a href={`mailto:${contactData.email}`} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors break-all">
                      {contactData.email}
                    </a>
                  </div>
                </div>
              )}

              {contactData.address && (
                <div className="flex items-start gap-3 group">
                  <div className="p-2 rounded-lg bg-muted group-hover:bg-[var(--brand-primary)]/10 transition-colors shrink-0">
                    <MapPin className="h-4 w-4 text-foreground group-hover:text-[var(--brand-primary)] transition-colors" />
                  </div>
                  <div className="pt-1.5">
                    <span className="text-sm font-medium text-muted-foreground leading-tight block">
                      {contactData.address}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {contactData.creci && (
              <div className="mt-6 pt-6 border-t border-border/50">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
                  CRECI: {contactData.creci}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-sm font-medium text-muted-foreground text-center md:text-left order-1">
            &copy; {currentYear} {branding.display_name || branding.company_name}. Todos os direitos reservados.
          </div>

          <div className="flex items-center gap-6 order-2">
            <Link to={getUrl('/privacidade')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Política de Privacidade
            </Link>
            <Link to={getUrl('/termos')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Termos de Uso
            </Link>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800/80 px-3 py-1.5 rounded-full hover:opacity-95 transition-all shadow-sm order-3">
            <span>Criado com</span>
            <a 
              href="https://imobpanda.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-1 hover:opacity-80 transition-opacity"
            >
              <span className="font-bold text-zinc-850 dark:text-white text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="text-[hsl(217,91%,40%)]">Imob</span>Panda
              </span>
              <img
                src={logoImage}
                alt="ImobPanda Logo"
                className="w-4 h-4 object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

